import { useEffect, useState } from 'react';

/**
 * A ring drawn around a live page element, plus a dimmed backdrop everywhere
 * else. Deliberately NOT a screenshot: the audit's §7 finding (injury
 * narratives, signature images, site photos) rules out capturing this page, and
 * FieldFlo's own help articles — annotated screenshots dated fifteen months ago
 * — show what happens to captured UI over time.
 *
 * The ring tracks the element on scroll and resize rather than being painted
 * once, because the whole point is that the user scrolls to look at it.
 */

interface SpotlightProps {
  target: Element | null;
  label?: string;
  onDismiss: () => void;
}

const PAD = 6;

export function Spotlight({ target, label, onDismiss }: SpotlightProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!target) { setRect(null); return; }

    const measure = () => setRect(target.getBoundingClientRect());
    measure();

    // Passive listeners on capture so nested scroll containers (mobile <main>)
    // are picked up too — the ring must not drift away from its element.
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    window.addEventListener('resize', measure, { passive: true });

    // ResizeObserver is nice-to-have (it catches a field growing as rows are
    // added) and happy-dom ships a stub whose instances have no observe(), so
    // this must never be the thing that breaks the highlight.
    let observer: ResizeObserver | null = null;
    try {
      observer = new ResizeObserver(measure);
      observer.observe(target);
    } catch {
      observer = null;
    }

    return () => {
      window.removeEventListener('scroll', measure, { capture: true });
      window.removeEventListener('resize', measure);
      try { observer?.disconnect(); } catch { /* same stub */ }
    };
  }, [target]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  if (!target || !rect) return null;

  return (
    <div
      data-testid="guide-spotlight"
      className="pointer-events-none fixed inset-0 z-[60]"
      aria-hidden="true"
    >
      {/* Backdrop: a single element with a huge spread shadow punches the hole,
          which keeps the DOM to one node and the paint to one layer. */}
      <div
        className="absolute rounded-lg ring-2 ring-safety-500 transition-all duration-200"
        style={{
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          boxShadow: '0 0 0 9999px rgba(2, 6, 12, 0.55)',
        }}
      />
      {label && (
        <span
          className="absolute rounded-md bg-safety-500 px-2 py-1 text-xs font-semibold text-white shadow-lg"
          style={{
            // Above the ring, unless that would go off-screen — then below it.
            top: rect.top - PAD > 34 ? rect.top - PAD - 28 : rect.bottom + PAD + 6,
            left: Math.max(8, rect.left - PAD),
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
